import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axios";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";
import "../styles/admin.css";

const CAMPAIGN_TYPES = [
  "Weekend Promo",
  "Holiday Trip",
  "Long-Distance Package",
  "New Customer Promo",
  "Repeat Customer Promo",
  "Low Utilization Promo",
  "Custom",
];

const TARGET_AUDIENCES = [
  "All customers",
  "New customers",
  "Repeat customers",
  "Fully verified customers",
  "Inactive customers",
];

const DISCOUNT_TYPES = [
  { value: "none", label: "None" },
  { value: "percentage", label: "Percentage" },
  { value: "fixed", label: "Fixed amount" },
];

const STATUSES = ["draft", "scheduled", "active", "published", "expired", "archived"];
const TRAVEL_THEMES = [
  "Family Trip",
  "Barkada Trip",
  "Business Trip",
  "Province Trip",
  "Weekend Getaway",
  "Custom",
];
const DESTINATION_THEMES = ["City", "Province", "Beach", "Mountain", "Long-Distance", "Custom"];
const BANNER_WIDTH = 1440;
const BANNER_HEIGHT = 500;
const BANNER_TEMPLATES = [
  { key: "premium_fleet", label: "Premium Fleet" },
  { key: "weekend_promo", label: "Weekend Promo" },
  { key: "long_distance", label: "Long-Distance Trip" },
  { key: "family_barkada", label: "Family / Barkada Trip" },
  { key: "minimal_orange", label: "Minimal Orange Banner" },
];

const emptyForm = {
  title: "",
  description: "",
  campaignType: "Weekend Promo",
  targetAudience: "All customers",
  vehicleCategory: "",
  travelTheme: "Weekend Getaway",
  destinationTheme: "City",
  selectedVehicles: [],
  discountType: "none",
  discountValue: "",
  promoCode: "",
  startDate: "",
  startTime: "00:00",
  endDate: "",
  endTime: "23:59",
  bannerText: "",
  ctaText: "",
  socialCaption: "",
  imageUrl: "",
  imageSource: "",
  image: null,
  status: "draft",
};

function formatDate(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? Math.round(number).toLocaleString() : "0";
}

function formatPeso(value) {
  const number = Number(value || 0);
  return `PHP ${Number.isFinite(number) ? Math.round(number).toLocaleString() : "0"}`;
}

function formatCampaignDiscount(campaign = {}) {
  const value = Number(campaign.discountValue || 0);
  if (!Number.isFinite(value) || value <= 0) return "None";
  if (campaign.discountType === "percentage") return `${value}%`;
  if (campaign.discountType === "fixed") return formatPeso(value);
  return "None";
}

function formatPercent(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return "0%";
  return `${number.toFixed(number >= 10 ? 1 : 2).replace(/\.0+$/, "")}%`;
}

function emptyPerformance(campaign = {}) {
  const views = Number(campaign.viewCount || 0);
  const clicks = Number(campaign.clickCount || 0);
  return {
    campaignId: campaign._id || "",
    views,
    clicks,
    ctr: views > 0 ? (clicks / views) * 100 : 0,
    bookingsFromCampaign: 0,
    revenueFromCampaign: 0,
    discountGiven: 0,
    averageBookingValue: 0,
    conversionRate: 0,
    insight: "No performance data yet. Views and clicks will appear once this campaign is shown to customers.",
    status: getCampaignStatus(campaign),
    activeDateRange: {
      startDate: campaign.startDate || null,
      endDate: campaign.endDate || null,
    },
  };
}

function toInputDate(value) {
  const parsedDate = parseCampaignDate(value);
  if (parsedDate) return parsedDate.input;
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  return formatDateInput(parsed);
}

function toInputTime(value, fallback = "") {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return fallback;
  return `${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;
}

function getTodayInputDate() {
  const today = new Date();
  return formatDateInput(today);
}

function formatDateInput(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseCampaignDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const dateOnly = raw.split("T")[0];
  const isoMatch = dateOnly.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const parts = isoMatch
    ? { year: Number(isoMatch[1]), month: Number(isoMatch[2]), day: Number(isoMatch[3]) }
    : slashMatch
    ? { year: Number(slashMatch[3]), month: Number(slashMatch[1]), day: Number(slashMatch[2]) }
    : null;

  if (!parts) return null;
  const date = new Date(parts.year, parts.month - 1, parts.day);
  if (
    date.getFullYear() !== parts.year ||
    date.getMonth() !== parts.month - 1 ||
    date.getDate() !== parts.day
  ) {
    return null;
  }
  return { date, input: formatDateInput(date), time: date.getTime() };
}

function parseCampaignTime(value, fallback = "00:00") {
  const raw = String(value || fallback || "00:00").trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/i);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.toLowerCase();

  if (meridiem) {
    if (hours < 1 || hours > 12 || minutes > 59) return null;
    hours = hours % 12;
    if (meridiem === "pm") hours += 12;
  } else if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function validateCampaignSchedule(formState) {
  const errors = {};
  const today = parseCampaignDate(getTodayInputDate());
  const start = parseCampaignDate(formState.startDate);
  const end = parseCampaignDate(formState.endDate);

  if (!start) {
    errors.startDate = "Start date must be valid.";
  } else if (today && start.time < today.time) {
    errors.startDate = "Start date cannot be in the past.";
  }

  if (!end) {
    errors.endDate = "End date must be valid.";
  } else if (start && end.time < start.time) {
    errors.endDate = "End date must be on or after the start date.";
  }

  if (start && end && start.time === end.time) {
    const startMinutes = parseCampaignTime(formState.startTime, "00:00");
    const endMinutes = parseCampaignTime(formState.endTime, "23:59");
    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
      errors.endTime = "End time must be after start time for same-day campaigns.";
    }
  }

  return errors;
}

function toCampaignDatePayload(dateValue) {
  const parsedDate = parseCampaignDate(dateValue);
  if (!parsedDate) return "";
  return parsedDate.input;
}

function sanitizeDiscountValue(value, discountType) {
  if (discountType === "none") return "";
  const clean = String(value || "").replace(/[^\d.]/g, "");
  if (!clean) return "";
  const numeric = Number(clean);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  if (discountType === "percentage" && numeric > 100) return "";
  return clean;
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getVehicleName(vehicle = {}) {
  return [vehicle.make, vehicle.model].filter(Boolean).join(" ") || "Vehicle";
}

function getVehicleImage(vehicle = {}) {
  return vehicle.image || vehicle.imageUrl || vehicle.mainImage || (Array.isArray(vehicle.images) ? vehicle.images[0] : "") || "";
}

function getCampaignStatus(campaign) {
  return campaign.computedStatus || campaign.status || "draft";
}

function trimText(value, limit) {
  const clean = String(value || "").replace(/\s+/g, " ").trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, Math.max(limit - 1, 0)).trim()}…`;
}

function getDiscountCopy(form) {
  const value = Number(form.discountValue || 0);
  if (form.discountType === "percentage" && value > 0) return `Save ${value}%`;
  if (form.discountType === "fixed" && value > 0) return `Save ₱${Math.round(value).toLocaleString()}`;
  return "";
}

function getBannerDiscountCopy(form) {
  const value = Number(form.discountValue || 0);
  if (form.discountType === "percentage" && value > 0) return `SAVE ${value}%`;
  if (form.discountType === "fixed" && value > 0) {
    return `SAVE PHP ${Math.round(value).toLocaleString()}`;
  }
  return form.promoCode ? "SPECIAL OFFER" : "FLEETX PROMO";
}

function getAudienceCopy(targetAudience) {
  const value = String(targetAudience || "").toLowerCase();
  if (value.includes("new")) return "Perfect for first-time FleetX bookings.";
  if (value.includes("repeat")) return "Welcome back and plan your next trip with FleetX.";
  if (value.includes("fully verified")) return "Built for customers who are ready for faster booking.";
  if (value.includes("inactive")) return "Come back and plan your next trip with FleetX.";
  return "Made for FleetX customers planning a reliable ride.";
}

function getThemeCopy(travelTheme, destinationTheme) {
  const travel = String(travelTheme || "").toLowerCase();
  const destination = String(destinationTheme || "").toLowerCase();
  if (travel.includes("family")) return "family trips";
  if (travel.includes("barkada")) return "barkada trips";
  if (travel.includes("business")) return "business travel";
  if (travel.includes("province") || destination.includes("province")) return "province trips";
  if (destination.includes("beach")) return "beach trips";
  if (destination.includes("mountain")) return "mountain getaways";
  if (destination.includes("long")) return "long-distance travel";
  return "weekend trips";
}

function generateCampaignContent(form, vehicles = []) {
  const selectedVehicle = vehicles.find((vehicle) => form.selectedVehicles.includes(String(vehicle._id)));
  const categoryVehicle = vehicles.find(
    (vehicle) => String(vehicle.category || "").toLowerCase() === String(form.vehicleCategory || "").toLowerCase() && getVehicleImage(vehicle)
  );
  const vehicleImage = getVehicleImage(selectedVehicle) || getVehicleImage(categoryVehicle);
  const category = form.vehicleCategory || selectedVehicle?.category || "vehicle";
  const categoryLabel = category === "vehicle" ? "Vehicle" : category;
  const discountCopy = getDiscountCopy(form);
  const audienceCopy = getAudienceCopy(form.targetAudience);
  const themeCopy = getThemeCopy(form.travelTheme, form.destinationTheme);
  const promoSuffix = form.promoCode ? ` Use code ${form.promoCode}.` : "";

  const templates = {
    "Weekend Promo": {
      title: `Weekend ${categoryLabel} Promo`,
      banner: `${discountCopy ? `${discountCopy}. ` : ""}Make your weekend trip more comfortable.`,
      description: `Book a ${categoryLabel.toLowerCase()} for your next weekend trip and enjoy a more spacious, flexible ride with FleetX.`,
      cta: "Book Your Weekend Ride",
      caption: `Planning a weekend trip with family or friends? FleetX makes it easier with reliable ${categoryLabel.toLowerCase()} options, flexible booking, and secure verification.${promoSuffix}`,
    },
    "Holiday Trip": {
      title: `Holiday ${categoryLabel} Offer`,
      banner: `${discountCopy ? `${discountCopy} for holiday trips. ` : ""}Make every holiday trip easier with FleetX.`,
      description: `Prepare for ${themeCopy} with a reliable ${categoryLabel.toLowerCase()} and a smoother FleetX reservation flow.`,
      cta: "Reserve Your Holiday Ride",
      caption: `Holiday plans coming up? FleetX helps you find a reliable ride for ${themeCopy}, with clear booking steps and verified account safety.${promoSuffix}`,
    },
    "Long-Distance Package": {
      title: `Long-Distance ${categoryLabel} Package`,
      banner: `${discountCopy ? `${discountCopy}. ` : ""}Travel farther with more space and comfort.`,
      description: `Book a ${categoryLabel.toLowerCase()} for your long-distance trip and enjoy a practical option for family, barkada, or group travel.`,
      cta: "Plan Your Long-Distance Trip",
      caption: `Going out of town? FleetX helps make long-distance travel easier with spacious vehicles and a guided booking process.${promoSuffix}`,
    },
    "New Customer Promo": {
      title: `First FleetX Trip ${categoryLabel} Promo`,
      banner: `${discountCopy ? `${discountCopy} on your first trip. ` : ""}Start your FleetX journey with confidence.`,
      description: `New to FleetX? Book a ${categoryLabel.toLowerCase()} and experience a guided, secure car rental process.`,
      cta: "Start Your First Booking",
      caption: `First time booking with FleetX? Choose a reliable ${categoryLabel.toLowerCase()} and enjoy a secure reservation experience.${promoSuffix}`,
    },
    "Repeat Customer Promo": {
      title: `Welcome Back ${categoryLabel} Promo`,
      banner: `${discountCopy ? `${discountCopy} for your next ride. ` : ""}Your next FleetX trip is waiting.`,
      description: `Plan another trip with FleetX and choose a ${categoryLabel.toLowerCase()} that fits your next route.`,
      cta: "Book Your Next Trip",
      caption: `Welcome back to FleetX. Whether it is for ${themeCopy} or daily travel, your next reliable ride is ready.${promoSuffix}`,
    },
    "Low Utilization Promo": {
      title: `${categoryLabel} Spotlight Promo`,
      banner: `${discountCopy ? `${discountCopy}. ` : ""}Discover a FleetX ride that deserves the spotlight.`,
      description: `Feature a ${categoryLabel.toLowerCase()} for customers looking for a practical and reliable rental option.`,
      cta: "Explore This Ride",
      caption: `Looking for a dependable ride? FleetX is featuring selected ${categoryLabel.toLowerCase()} options for your next trip.${promoSuffix}`,
    },
  };

  const base = templates[form.campaignType] || {
    title: `${categoryLabel} FleetX Promo`,
    banner: `${discountCopy ? `${discountCopy}. ` : ""}Plan your next trip with FleetX.`,
    description: `Create a FleetX campaign for ${themeCopy} with clear booking steps and reliable vehicle options.`,
    cta: "Book with FleetX",
    caption: `Plan your next trip with FleetX and choose a reliable ${categoryLabel.toLowerCase()} for your route.${promoSuffix}`,
  };

  return {
    title: trimText(base.title, 80),
    bannerText: trimText(base.banner, 120),
    description: trimText(`${base.description} ${audienceCopy}`, 220),
    ctaText: trimText(base.cta, 40),
    socialCaption: trimText(base.caption, 260),
    imageUrl: vehicleImage,
  };
}

function getBannerText(form) {
  return String(form.bannerText || form.description || "Book your next FleetX trip with confidence.").trim();
}

function getBannerVehicleImage(form, vehicles = []) {
  const selectedVehicle = vehicles.find((vehicle) => form.selectedVehicles.includes(String(vehicle._id)));
  const categoryVehicle = vehicles.find(
    (vehicle) => String(vehicle.category || "").toLowerCase() === String(form.vehicleCategory || "").toLowerCase() && getVehicleImage(vehicle)
  );
  return getVehicleImage(selectedVehicle) || getVehicleImage(categoryVehicle) || "";
}

function clampText(value, limit) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, Math.max(limit - 3, 0)).trim()}...`;
}

function loadCanvasImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function wrapCanvasText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);

  const visibleLines = lines.slice(0, maxLines);
  if (lines.length > maxLines && visibleLines.length) {
    const lastIndex = visibleLines.length - 1;
    let last = visibleLines[lastIndex];
    while (last.length > 3 && ctx.measureText(`${last}...`).width > maxWidth) {
      last = last.slice(0, -1).trim();
    }
    visibleLines[lastIndex] = `${last}...`;
  }

  visibleLines.forEach((line, index) => {
    ctx.fillText(line, x, y + index * lineHeight);
  });

  return visibleLines.length * lineHeight;
}

function drawContainImage(ctx, image, x, y, width, height) {
  if (!image) return;
  const scale = Math.min(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const drawX = x + (width - drawWidth) / 2;
  const drawY = y + (height - drawHeight) / 2;
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
}

function getBannerTemplateDesign(templateKey) {
  const templates = {
    premium_fleet: {
      eyebrow: "PREMIUM FLEET",
      colors: ["#07111f", "#111827", "#f97316"],
      accent: "#f97316",
      accentSoft: "#fed7aa",
      text: "#ffffff",
      muted: "rgba(255, 255, 255, 0.86)",
      card: "rgba(2, 6, 23, 0.34)",
      panel: ["rgba(255, 255, 255, 0.12)", "rgba(249, 115, 22, 0.18)"],
      pattern: "premium",
    },
    weekend_promo: {
      eyebrow: "WEEKEND PROMO",
      colors: ["#351307", "#c2410c", "#fb923c"],
      accent: "#fb923c",
      accentSoft: "#ffedd5",
      text: "#ffffff",
      muted: "rgba(255, 247, 237, 0.9)",
      card: "rgba(67, 20, 7, 0.32)",
      panel: ["rgba(255, 247, 237, 0.18)", "rgba(251, 146, 60, 0.22)"],
      pattern: "sun",
    },
    long_distance: {
      eyebrow: "LONG-DISTANCE TRIP",
      colors: ["#0f172a", "#1f2937", "#ea580c"],
      accent: "#f97316",
      accentSoft: "#fed7aa",
      text: "#ffffff",
      muted: "rgba(226, 232, 240, 0.9)",
      card: "rgba(15, 23, 42, 0.36)",
      panel: ["rgba(148, 163, 184, 0.14)", "rgba(249, 115, 22, 0.16)"],
      pattern: "route",
    },
    family_barkada: {
      eyebrow: "FAMILY / BARKADA",
      colors: ["#5f1d0b", "#ea580c", "#fed7aa"],
      accent: "#fdba74",
      accentSoft: "#fff7ed",
      text: "#ffffff",
      muted: "rgba(255, 247, 237, 0.9)",
      card: "rgba(124, 45, 18, 0.28)",
      panel: ["rgba(255, 247, 237, 0.22)", "rgba(253, 186, 116, 0.2)"],
      pattern: "friendly",
    },
    minimal_orange: {
      eyebrow: "MINIMAL ORANGE",
      colors: ["#fff7ed", "#fed7aa", "#f97316"],
      accent: "#ea580c",
      accentSoft: "#431407",
      text: "#111827",
      muted: "#7c2d12",
      card: "rgba(255, 255, 255, 0.74)",
      panel: ["rgba(255, 255, 255, 0.68)", "rgba(255, 237, 213, 0.78)"],
      pattern: "minimal",
    },
  };
  return templates[templateKey] || templates.premium_fleet;
}

function drawBannerPattern(ctx, design) {
  ctx.save();
  if (design.pattern === "route") {
    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 3;
    ctx.setLineDash([18, 16]);
    for (let i = -140; i < 1460; i += 210) {
      ctx.beginPath();
      ctx.moveTo(i, 468);
      ctx.bezierCurveTo(i + 100, 330, i + 270, 300, i + 390, 110);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.fillStyle = "#fed7aa";
    [900, 1120, 1290].forEach((x, index) => {
      ctx.beginPath();
      ctx.arc(x, 120 + index * 95, 9, 0, Math.PI * 2);
      ctx.fill();
    });
  } else if (design.pattern === "sun") {
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 7;
    for (let radius = 92; radius <= 270; radius += 44) {
      ctx.beginPath();
      ctx.arc(1190, 130, radius, Math.PI * 0.08, Math.PI * 1.25);
      ctx.stroke();
    }
  } else if (design.pattern === "friendly") {
    ctx.globalAlpha = 0.17;
    ctx.fillStyle = "#ffffff";
    [
      [1060, 88, 132],
      [1250, 366, 230],
      [840, 418, 118],
    ].forEach(([x, y, radius]) => {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    });
  } else if (design.pattern === "minimal") {
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = "#9a3412";
    ctx.lineWidth = 4;
    for (let i = -220; i < 1550; i += 120) {
      ctx.beginPath();
      ctx.moveTo(i, 520);
      ctx.lineTo(i + 360, -40);
      ctx.stroke();
    }
  } else {
    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    for (let x = 780; x < 1420; x += 42) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x - 190, 500);
      ctx.stroke();
    }
  }
  ctx.restore();
}

async function renderCampaignBanner({ form, vehicles, templateKey, imageSource, customImageUrl }) {
  const canvas = document.createElement("canvas");
  canvas.width = BANNER_WIDTH;
  canvas.height = BANNER_HEIGHT;
  const ctx = canvas.getContext("2d");
  const design = getBannerTemplateDesign(templateKey);
  const imageUrl =
    imageSource === "custom"
      ? customImageUrl
      : imageSource === "vehicle"
      ? getBannerVehicleImage(form, vehicles)
      : "";
  const image = await loadCanvasImage(imageUrl);

  const gradient = ctx.createLinearGradient(0, 0, BANNER_WIDTH, BANNER_HEIGHT);
  design.colors.forEach((color, index) => {
    gradient.addColorStop(index / (design.colors.length - 1), color);
  });
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, BANNER_WIDTH, BANNER_HEIGHT);
  drawBannerPattern(ctx, design);

  ctx.save();
  ctx.globalAlpha = 0.2;
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(1325, 48, 190, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const vehiclePanel = { x: 770, y: 56, width: 590, height: 384, padding: 44 };
  const panelGradient = ctx.createLinearGradient(vehiclePanel.x, vehiclePanel.y, vehiclePanel.x + vehiclePanel.width, vehiclePanel.y + vehiclePanel.height);
  panelGradient.addColorStop(0, design.panel[0]);
  panelGradient.addColorStop(1, design.panel[1]);
  ctx.save();
  ctx.shadowColor = "rgba(15, 23, 42, 0.32)";
  ctx.shadowBlur = 34;
  ctx.shadowOffsetY = 18;
  ctx.fillStyle = panelGradient;
  roundRect(ctx, vehiclePanel.x, vehiclePanel.y, vehiclePanel.width, vehiclePanel.height, 42);
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.34)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, vehiclePanel.x, vehiclePanel.y, vehiclePanel.width, vehiclePanel.height, 42);
  ctx.stroke();
  if (image) {
    drawContainImage(
      ctx,
      image,
      vehiclePanel.x + vehiclePanel.padding,
      vehiclePanel.y + vehiclePanel.padding,
      vehiclePanel.width - vehiclePanel.padding * 2,
      vehiclePanel.height - vehiclePanel.padding * 2
    );
  } else {
    ctx.fillStyle = design.pattern === "minimal" ? "rgba(124, 45, 18, 0.2)" : "rgba(255, 255, 255, 0.28)";
    ctx.font = "900 58px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("FLEETX", vehiclePanel.x + vehiclePanel.width / 2, vehiclePanel.y + vehiclePanel.height / 2 + 18);
    ctx.textAlign = "left";
  }
  ctx.restore();

  const title = clampText(form.title || `${form.campaignType || "FleetX"} Promo`, 86);
  const body = clampText(getBannerText(form), 132);
  const discount = clampText(getBannerDiscountCopy(form), 19);
  const titleFont = title.length > 62 ? 50 : title.length > 42 ? 56 : 62;
  const textX = 92;
  const textMaxWidth = 590;

  ctx.save();
  ctx.fillStyle = design.card;
  roundRect(ctx, 56, 46, 666, 408, 36);
  ctx.fill();
  ctx.strokeStyle = design.pattern === "minimal" ? "rgba(249, 115, 22, 0.2)" : "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = 1.4;
  roundRect(ctx, 56, 46, 666, 408, 36);
  ctx.stroke();
  ctx.restore();

  ctx.fillStyle = design.pattern === "minimal" ? "#c2410c" : "#fed7aa";
  ctx.font = "900 23px Arial, sans-serif";
  ctx.fillText("FLEETX", textX, 98);

  ctx.fillStyle = design.accentSoft;
  ctx.font = "900 15px Arial, sans-serif";
  const eyebrowWidth = Math.min(ctx.measureText(design.eyebrow).width + 30, 250);
  roundRect(ctx, textX, 116, eyebrowWidth, 34, 17);
  ctx.fill();
  ctx.fillStyle = design.pattern === "minimal" ? "#ffffff" : "#7c2d12";
  ctx.fillText(design.eyebrow, textX + 15, 139);

  ctx.fillStyle = design.text;
  ctx.font = `900 ${titleFont}px Arial, sans-serif`;
  const titleHeight = wrapCanvasText(ctx, title, textX, 205, textMaxWidth, titleFont + 6, 2);

  ctx.fillStyle = design.muted;
  ctx.font = "700 25px Arial, sans-serif";
  wrapCanvasText(ctx, body, textX, 224 + titleHeight, 555, 33, 2);

  ctx.save();
  ctx.shadowColor = "rgba(15, 23, 42, 0.24)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = design.pattern === "minimal" ? "#111827" : "#fff7ed";
  roundRect(ctx, 1032, 48, 292, 86, 28);
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = design.pattern === "minimal" ? "#ffffff" : "#c2410c";
  ctx.font = "900 30px Arial, sans-serif";
  ctx.fillText(discount, 1062, 101);

  if (form.promoCode) {
    ctx.fillStyle = design.pattern === "minimal" ? "rgba(17, 24, 39, 0.82)" : "rgba(255, 255, 255, 0.92)";
    ctx.font = "800 23px Arial, sans-serif";
    ctx.fillText(`Use code ${clampText(form.promoCode, 18)}`, 1062, 166);
  }

  if (form.vehicleCategory) {
    ctx.fillStyle = design.pattern === "minimal" ? "#7c2d12" : "rgba(255, 255, 255, 0.88)";
    ctx.font = "800 22px Arial, sans-serif";
    ctx.fillText(clampText(form.vehicleCategory, 24), 1062, 424);
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.94));
  if (!blob) throw new Error("Unable to generate campaign banner.");
  const file = new File([blob], `fleetx-campaign-banner-${Date.now()}.jpg`, { type: "image/jpeg" });
  return {
    file,
    previewUrl: URL.createObjectURL(blob),
  };
}

export default function MarketingCampaigns() {
  const { user } = useAuth();
  const canManage = user?.role === "technical_admin" || user?.role === "admin";
  const [campaigns, setCampaigns] = useState([]);
  const [performanceByCampaign, setPerformanceByCampaign] = useState({});
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [confirmAction, setConfirmAction] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState("");
  const [modalTab, setModalTab] = useState("details");
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const fileInputRef = useRef(null);
  const [contentPreview, setContentPreview] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [bannerTemplate, setBannerTemplate] = useState("premium_fleet");
  const [bannerImageSource, setBannerImageSource] = useState("vehicle");
  const [bannerCustomImageUrl, setBannerCustomImageUrl] = useState("");
  const [bannerPreview, setBannerPreview] = useState(null);
  const [generatingBanner, setGeneratingBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const formInitialRef = useState(null);
  const selectedImagePreviewUrl = useMemo(
    () => (form.image ? URL.createObjectURL(form.image) : ""),
    [form.image]
  );

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");
      const [campaignRes, vehicleRes, performanceRes] = await Promise.all([
        api.get("/admin/marketing-campaigns"),
        api.get("/admin/vehicles"),
        api.get("/admin/marketing-campaigns/performance").catch((err) => {
          console.warn("Campaign performance unavailable:", err);
          return null;
        }),
      ]);
      setCampaigns(Array.isArray(campaignRes.data?.campaigns) ? campaignRes.data.campaigns : []);
      const performanceList = Array.isArray(performanceRes?.data?.performance)
        ? performanceRes.data.performance
        : [];
      setPerformanceByCampaign(
        performanceList.reduce((acc, item) => {
          acc[item.campaignId] = item;
          return acc;
        }, {})
      );
      setPerformanceSummary(performanceRes?.data?.summary || null);
      const vehicleList = Array.isArray(vehicleRes.data?.vehicles)
        ? vehicleRes.data.vehicles
        : Array.isArray(vehicleRes.data)
        ? vehicleRes.data
        : [];
      setVehicles(vehicleList);
    } catch (err) {
      setMessage(err?.response?.data?.message || "Unable to load campaigns. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    return () => {
      if (bannerCustomImageUrl) URL.revokeObjectURL(bannerCustomImageUrl);
      if (bannerPreview?.previewUrl) URL.revokeObjectURL(bannerPreview.previewUrl);
    };
  }, [bannerCustomImageUrl, bannerPreview]);

  useEffect(() => {
    return () => {
      if (selectedImagePreviewUrl) URL.revokeObjectURL(selectedImagePreviewUrl);
    };
  }, [selectedImagePreviewUrl]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(vehicles.map((vehicle) => String(vehicle.category || "").trim()).filter(Boolean))
    ).sort();
  }, [vehicles]);

  const getCampaignPerformance = useCallback(
    (campaign) => performanceByCampaign[campaign?._id] || emptyPerformance(campaign),
    [performanceByCampaign]
  );

  const openCreateForm = () => {
    setEditingCampaign(null);
    setForm(emptyForm);
    setErrors({});
    setMessage("");
    setContentPreview(null);
    setBannerPreview(null);
    setBannerCustomImageUrl("");
    setModalTab("details");
    setDiscardConfirmOpen(false);
    setVehicleSearch("");
    setFormOpen(true);
  };

  const openEditForm = (campaign) => {
    setEditingCampaign(campaign);
    setForm({
      ...emptyForm,
      title: campaign.title || "",
      description: campaign.description || "",
      campaignType: campaign.campaignType || "Custom",
      targetAudience: campaign.targetAudience || "All customers",
      vehicleCategory: campaign.vehicleCategory || "",
      travelTheme: campaign.travelTheme || "Weekend Getaway",
      destinationTheme: campaign.destinationTheme || "City",
      selectedVehicles: (campaign.selectedVehicles || []).map((vehicle) => String(vehicle._id || vehicle)),
      discountType: campaign.discountType || "none",
      discountValue: sanitizeDiscountValue(
        campaign.discountValue ? String(campaign.discountValue) : "",
        campaign.discountType || "none"
      ),
      promoCode: (campaign.discountType || "none") === "none" ? "" : campaign.promoCode || "",
      startDate: toInputDate(campaign.startDate),
      startTime: toInputTime(campaign.startDate, "00:00"),
      endDate: toInputDate(campaign.endDate),
      endTime: toInputTime(campaign.endDate, "23:59"),
      bannerText: campaign.bannerText || "",
      ctaText: campaign.ctaText || "",
      socialCaption: campaign.socialCaption || "",
      imageUrl: campaign.imageUrl || "",
      imageSource: campaign.imageSource || "",
      image: null,
      status: campaign.status || "draft",
    });
    setErrors({});
    setMessage("");
    setContentPreview(null);
    setBannerPreview(null);
    setBannerCustomImageUrl("");
    setModalTab("details");
    setDiscardConfirmOpen(false);
    setVehicleSearch("");
    setFormOpen(true);
  };

  const requestCloseModal = () => {
    setDiscardConfirmOpen(true);
  };

  const confirmCloseModal = () => {
    setDiscardConfirmOpen(false);
    setFormOpen(false);
  };

  const updateField = (field, value) => {
    const today = getTodayInputDate();
    setForm((current) => {
      if (field === "discountType") {
        const next = { ...current, discountType: value };
        if (value === "none") {
          next.discountValue = "";
          next.promoCode = "";
        } else {
          next.discountValue = sanitizeDiscountValue(current.discountValue, value);
        }
        return next;
      }

      if (field === "discountValue") {
        return {
          ...current,
          discountValue: sanitizeDiscountValue(value, current.discountType),
        };
      }

      const parsedToday = parseCampaignDate(today);
      const parsedValue =
        field === "startDate" || field === "endDate" ? parseCampaignDate(value) : null;
      const normalizedDateValue = parsedValue
        ? parsedValue.time < parsedToday.time
          ? today
          : parsedValue.input
        : value;
      const nextValue = field === "startDate" || field === "endDate" ? normalizedDateValue : value;
      const next = { ...current, [field]: nextValue };

      const parsedNextStart = parseCampaignDate(next.startDate);
      const parsedNextEnd = parseCampaignDate(next.endDate);

      if (field === "startDate" && parsedNextStart && parsedNextEnd && parsedNextEnd.time < parsedNextStart.time) {
        next.endDate = nextValue;
      }
      if (field === "endDate" && parsedNextStart && parsedNextEnd && parsedNextEnd.time < parsedNextStart.time) {
        next.startDate = nextValue;
      }

      return next;
    });
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const handleVehicleSelect = (event) => {
    updateField(
      "selectedVehicles",
      Array.from(event.target.selectedOptions).map((option) => option.value)
    );
  };

  const generateContent = () => {
    if (!canManage) return;
    setGeneratingContent(true);
    window.setTimeout(() => {
      setContentPreview(generateCampaignContent(form, vehicles));
      setGeneratingContent(false);
    }, 150);
  };

  const applyGeneratedContent = () => {
    if (!contentPreview) return;
    setForm((current) => ({
      ...current,
      title: contentPreview.title,
      description: contentPreview.description,
      bannerText: contentPreview.bannerText,
      ctaText: contentPreview.ctaText,
      socialCaption: contentPreview.socialCaption,
      imageUrl: contentPreview.imageUrl || current.imageUrl,
    }));
    setContentPreview(null);
  };

  const openBannerGenerator = () => {
    if (!canManage) return;
    const hasVehicleImage = Boolean(getBannerVehicleImage(form, vehicles));
    setBannerImageSource(hasVehicleImage ? "vehicle" : "gradient");
    setBannerTemplate("premium_fleet");
    setBannerMessage(
      hasVehicleImage
        ? ""
        : "No vehicle image available. A branded background will be used."
    );
    setBannerPreview(null);
  };

  const handleBannerCustomImage = (file) => {
    if (bannerCustomImageUrl) URL.revokeObjectURL(bannerCustomImageUrl);
    if (!file) {
      setBannerCustomImageUrl("");
      return;
    }
    const nextUrl = URL.createObjectURL(file);
    setBannerCustomImageUrl(nextUrl);
    setBannerImageSource("custom");
  };

  const generateBannerPreview = async () => {
    if (!canManage) return;
    try {
      setGeneratingBanner(true);
      setBannerMessage("");
      if (bannerPreview?.previewUrl) URL.revokeObjectURL(bannerPreview.previewUrl);
      const rendered = await renderCampaignBanner({
        form,
        vehicles,
        templateKey: bannerTemplate,
        imageSource: bannerImageSource,
        customImageUrl: bannerCustomImageUrl,
      });
      setBannerPreview(rendered);
      if (bannerImageSource === "vehicle" && !getBannerVehicleImage(form, vehicles)) {
        setBannerMessage("No vehicle image available. A branded background was used.");
      }
    } catch (err) {
      setBannerMessage(err?.message || "Unable to generate banner. Please try again.");
      setBannerPreview(null);
    } finally {
      setGeneratingBanner(false);
    }
  };

  const applyGeneratedBanner = () => {
    if (!bannerPreview?.file) return;
    setForm((current) => ({
      ...current,
      image: bannerPreview.file,
      imageUrl: "",
      imageSource: "generated",
    }));
    setBannerMessage("Banner selected for this campaign. Save the campaign to upload it.");
  };

  const buildFormData = () => {
    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "image") return;
      if (key === "promoCode" && form.discountType === "none") {
        data.append(key, "");
        return;
      }
      if (key === "discountValue") {
        data.append(key, form.discountType === "none" ? "" : Number(value || 0));
        return;
      }
      if (key === "startTime" || key === "endTime") return;
      if (key === "startDate") {
        data.append(key, toCampaignDatePayload(form.startDate));
        return;
      }
      if (key === "endDate") {
        data.append(key, toCampaignDatePayload(form.endDate));
        return;
      }
      if (key === "selectedVehicles") {
        data.append(key, JSON.stringify(value || []));
        return;
      }
      data.append(key, value ?? "");
    });
    if (form.image) data.append("image", form.image);
    return data;
  };

  const saveCampaign = async (event) => {
    event.preventDefault();
    if (!canManage) return;

    const scheduleErrors = validateCampaignSchedule(form);
    if (Object.keys(scheduleErrors).length) {
      setErrors(scheduleErrors);
      setMessage("Please fix the campaign schedule before saving.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      setErrors({});
      const payload = buildFormData();
      const res = editingCampaign
        ? await api.patch(`/admin/marketing-campaigns/${editingCampaign._id}`, payload)
        : await api.post("/admin/marketing-campaigns", payload);
      setMessage(res.data?.message || "Campaign saved.");
      setFormOpen(false);
      await loadCampaigns();
    } catch (err) {
      setErrors(err?.response?.data?.errors || {});
      setMessage(err?.response?.data?.message || "Unable to save campaign. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (campaign, status) => {
    if (!canManage || statusUpdatingId) return;

    try {
      setMessage("");
      setStatusUpdatingId(campaign._id);
      await api.patch(`/admin/marketing-campaigns/${campaign._id}/status`, { status });
      await loadCampaigns();
      setMessage(`Campaign ${status === "active" ? "activated" : status === "archived" ? "archived" : "paused"} successfully.`);
    } catch (err) {
      setMessage(err?.response?.data?.message || "Unable to update campaign status. Please try again.");
    } finally {
      setStatusUpdatingId("");
    }
  };

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      const status = getCampaignStatus(campaign);
      if (filterStatus !== "all" && status !== filterStatus) return false;
      if (filterType !== "all" && campaign.campaignType !== filterType) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (
          !String(campaign.title || "").toLowerCase().includes(q) &&
          !String(campaign.promoCode || "").toLowerCase().includes(q) &&
          !String(campaign.description || "").toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [campaigns, filterSearch, filterStatus, filterType]);

  const requestStatusChange = (campaign, status, label) => {
    setConfirmAction({ campaign, status, label });
  };

  const confirmStatusChange = async () => {
    if (!confirmAction) return;
    const { campaign, status } = confirmAction;
    setConfirmAction(null);
    await updateStatus(campaign, status);
  };

  const editingPerformance = editingCampaign ? getCampaignPerformance(editingCampaign) : null;
  const marketingLoadingTitle = saving
    ? "Saving campaign..."
    : statusUpdatingId
    ? "Updating campaign status..."
    : generatingBanner
    ? "Generating banner..."
    : "";

  return (
    <AdminShell
      pageClassName="admin-shell--marketing"
      eyebrow="CUSTOMER GROWTH"
      title="Marketing Campaigns"
      subtitle="Create promos, feature vehicles, and manage customer-facing campaign content."
      actions={
        canManage ? (
          <button type="button" className="marketing-primary-action" onClick={openCreateForm}>
            New Campaign
          </button>
        ) : null
      }
    >
      <div className="marketing-page">
        <AdminLoadingOverlay
          open={Boolean(marketingLoadingTitle)}
          title={marketingLoadingTitle}
          message="Please wait while FleetX updates the campaign."
        />
        {message ? <div className="marketing-message">{message}</div> : null}

        {/* ── Performance Overview ── */}
        {performanceSummary ? (
          <section className="marketing-section">
            <div className="marketing-section__head">
              <h3>Performance Overview</h3>
            </div>
            <div className="marketing-overview-grid">
              <article className="marketing-overview-card">
                <span>Active Campaigns</span>
                <strong>{formatNumber(performanceSummary.activeCampaignsCount)}</strong>
              </article>
              <article className="marketing-overview-card">
                <span>Total Views</span>
                <strong>{formatNumber(performanceSummary.totalViews)}</strong>
              </article>
              <article className="marketing-overview-card">
                <span>Total Clicks</span>
                <strong>{formatNumber(performanceSummary.totalClicks)}</strong>
              </article>
              <article className="marketing-overview-card">
                <span>Average CTR</span>
                <strong>{formatPercent(performanceSummary.averageCtr)}</strong>
              </article>
              <article className="marketing-overview-card marketing-overview-card--revenue">
                <span>Campaign Revenue</span>
                <strong>{formatPeso(performanceSummary.totalRevenue)}</strong>
              </article>
            </div>
          </section>
        ) : null}

        {/* ── Campaigns List ── */}
        <section className="marketing-section">
          <div className="marketing-section__head">
            <h3>Campaigns</h3>
            <div className="marketing-filters">
              <input
                type="search"
                className="marketing-filter-search"
                placeholder="Search campaigns…"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
              <select
                className="marketing-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{titleCase(s)}</option>
                ))}
              </select>
              <select
                className="marketing-filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All types</option>
                {CAMPAIGN_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="marketing-empty">Loading campaigns…</div>
          ) : filteredCampaigns.length ? (
            <div className={`marketing-campaign-grid${filteredCampaigns.length === 1 ? " marketing-campaign-grid--single" : ""}`}>
              {filteredCampaigns.map((campaign) => {
                const status = getCampaignStatus(campaign);
                const performance = getCampaignPerformance(campaign);
                const isActive = campaign.status === "active";
                const isArchived = campaign.status === "archived";
                const descriptionText = String(campaign.description || campaign.bannerText || "").trim();
                return (
                  <article key={campaign._id} className="marketing-card">
                    <div className="marketing-card__media">
                      {campaign.imageUrl ? (
                        <img
                          src={campaign.imageUrl}
                          alt={campaign.title}
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.closest(".marketing-card__media")?.classList.add("marketing-card__media--fallback");
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span>{campaign.title?.charAt(0) || "M"}</span>
                      )}
                      {campaign.imageUrl ? (
                        <span className="marketing-card__media-fallback">{campaign.title?.charAt(0) || "M"}</span>
                      ) : null}
                    </div>

                    <div className="marketing-card__body">
                      <header className="marketing-card__header">
                        <div className="marketing-card__title-row">
                          <h2>{campaign.title}</h2>
                          <span className={`marketing-status marketing-status--${status}`}>
                            {titleCase(status)}
                          </span>
                        </div>
                        <p className="marketing-card__desc">
                          {descriptionText || "No campaign description provided."}
                        </p>
                      </header>

                      <div className="marketing-card__details">
                        <div className="marketing-detail-row">
                          <div><span>Type</span><strong>{campaign.campaignType}</strong></div>
                          <div><span>Audience</span><strong>{campaign.targetAudience}</strong></div>
                          <div><span>Discount</span><strong>{formatCampaignDiscount(campaign)}</strong></div>
                          <div><span>Promo Code</span><strong>{campaign.promoCode || "None"}</strong></div>
                        </div>
                        <div className="marketing-detail-row">
                          <div className="marketing-detail-row__full"><span>Date Range</span><strong>{formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}</strong></div>
                        </div>
                      </div>

                      <div className="marketing-card__perf">
                        <div><span>Views</span><strong>{formatNumber(performance.views)}</strong></div>
                        <div><span>Clicks</span><strong>{formatNumber(performance.clicks)}</strong></div>
                        <div><span>CTR</span><strong>{formatPercent(performance.ctr)}</strong></div>
                        <div><span>Bookings</span><strong>{formatNumber(performance.bookingsFromCampaign)}</strong></div>
                        <div><span>Revenue</span><strong>{formatPeso(performance.revenueFromCampaign)}</strong></div>
                        <div><span>Discount Given</span><strong>{formatPeso(performance.discountGiven)}</strong></div>
                      </div>

                      {(campaign.lastClickedAt || campaign.lastViewedAt) ? (
                        <p className="marketing-card__engagement">
                          Last engagement: {formatDate(campaign.lastClickedAt || campaign.lastViewedAt)}
                        </p>
                      ) : null}

                      <div className="marketing-actions">
                        <button
                          type="button"
                          className="marketing-btn marketing-btn--primary"
                          onClick={() => openEditForm(campaign)}
                        >
                          {canManage ? "View / Edit" : "View"}
                        </button>
                        {canManage ? (
                          <>
                            {!isActive ? (
                              <button
                                type="button"
                                className="marketing-btn marketing-btn--activate"
                                onClick={() => requestStatusChange(campaign, "active", "Activate")}
                                disabled={Boolean(statusUpdatingId)}
                              >
                                {statusUpdatingId === campaign._id ? "Updating..." : "Activate"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="marketing-btn marketing-btn--pause"
                                onClick={() => requestStatusChange(campaign, "draft", "Pause Campaign")}
                                disabled={Boolean(statusUpdatingId)}
                              >
                                {statusUpdatingId === campaign._id ? "Updating..." : "Pause"}
                              </button>
                            )}
                            {!isArchived ? (
                              <button
                                type="button"
                                className="marketing-btn marketing-btn--archive"
                                onClick={() => requestStatusChange(campaign, "archived", "Archive")}
                                disabled={Boolean(statusUpdatingId)}
                              >
                                {statusUpdatingId === campaign._id ? "Updating..." : "Archive"}
                              </button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : campaigns.length ? (
            <div className="marketing-empty">No campaigns match the current filters.</div>
          ) : (
            <div className="marketing-empty">No campaigns yet. Create your first campaign to get started.</div>
          )}
        </section>

        {/* ── Status Change Confirmation Dialog ── */}
        {confirmAction ? (
          <div
            className="marketing-confirm-overlay"
            role="presentation"
            onClick={() => !statusUpdatingId && setConfirmAction(null)}
          >
            <div
              className="marketing-confirm-box"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <h4>Confirm: {confirmAction.label}</h4>
              <p>
                {confirmAction.status === "archived"
                  ? `Archive "${confirmAction.campaign.title}"? Archived campaigns are hidden from customers.`
                  : confirmAction.status === "active"
                  ? `Activate "${confirmAction.campaign.title}"? It will become visible to customers.`
                  : `Pause "${confirmAction.campaign.title}"? It will be set to draft and hidden from customers.`}
              </p>
              <div className="marketing-confirm-box__actions">
                <button type="button" className="marketing-btn" onClick={() => setConfirmAction(null)} disabled={Boolean(statusUpdatingId)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`marketing-btn ${confirmAction.status === "archived" ? "marketing-btn--archive" : "marketing-btn--primary"}`}
                  onClick={confirmStatusChange}
                  disabled={Boolean(statusUpdatingId)}
                >
                  {statusUpdatingId ? "Updating..." : confirmAction.label}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {formOpen ? (
          <div
            className="marketing-modal-overlay"
            role="presentation"
            onMouseDown={requestCloseModal}
          >
            <form
              className="marketing-modal marketing-modal--tabbed"
              onSubmit={saveCampaign}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {/* ── Modal header ── */}
              <header className="marketing-modal__head">
                <div className="marketing-modal__head-title">
                  <span>Marketing Campaign</span>
                  <h2>{editingCampaign ? "Edit Campaign" : "Create Campaign"}</h2>
                </div>
                <button
                  type="button"
                  className="marketing-modal__close"
                  onClick={requestCloseModal}
                  aria-label="Close modal"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                </button>
              </header>

              {/* ── Tab bar ── */}
              <div className="marketing-modal__tabs" role="tablist">
                {[
                  { key: "details", label: "Details" },
                  { key: "content", label: "Content" },
                  { key: "banner", label: "Banner" },
                  ...(editingCampaign ? [{ key: "performance", label: "Performance" }] : []),
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={modalTab === tab.key}
                    className={`marketing-modal__tab${modalTab === tab.key ? " is-active" : ""}`}
                    onClick={() => setModalTab(tab.key)}
                  >
                    {tab.label}
                    {tab.key === "content" && contentPreview ? (
                      <span className="marketing-modal__tab-dot" aria-label="Has content preview" />
                    ) : null}
                    {tab.key === "banner" && bannerPreview ? (
                      <span className="marketing-modal__tab-dot" aria-label="Has banner preview" />
                    ) : null}
                  </button>
                ))}
              </div>

              {/* ── Tab: Details ── */}
              {modalTab === "details" ? (
                <div className="marketing-form-grid marketing-modal__body">
                  <div className="marketing-details-section-head is-first">
                    <span>1</span>
                    <div><h3>Campaign Basics</h3><p>Set the campaign identity, type, and status.</p></div>
                  </div>
                  <label className="marketing-field marketing-field--full">
                    Campaign title
                    <input value={form.title} onChange={(event) => updateField("title", event.target.value)} disabled={!canManage} />
                    {errors.title ? <small>{errors.title}</small> : null}
                  </label>

                  <label>
                    Campaign type
                    <select value={form.campaignType} onChange={(event) => updateField("campaignType", event.target.value)} disabled={!canManage}>
                      {CAMPAIGN_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <label>
                    Status
                    <select value={form.status} onChange={(event) => updateField("status", event.target.value)} disabled={!canManage}>
                      {STATUSES.map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}
                    </select>
                    {errors.status ? <small>{errors.status}</small> : null}
                  </label>
                  <div className="marketing-details-section-head">
                    <span>2</span>
                    <div><h3>Audience &amp; Targeting</h3><p>Define who the campaign reaches and what it promotes.</p></div>
                  </div>
                  <label>
                    Target audience
                    <select value={form.targetAudience} onChange={(event) => updateField("targetAudience", event.target.value)} disabled={!canManage}>
                      {TARGET_AUDIENCES.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <label>
                    Vehicle category
                    <select value={form.vehicleCategory} onChange={(event) => updateField("vehicleCategory", event.target.value)} disabled={!canManage}>
                      <option value="">Any category</option>
                      {categories.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <label>
                    Travel theme
                    <select value={form.travelTheme} onChange={(event) => updateField("travelTheme", event.target.value)} disabled={!canManage}>
                      {TRAVEL_THEMES.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <label>
                    Destination theme
                    <select value={form.destinationTheme} onChange={(event) => updateField("destinationTheme", event.target.value)} disabled={!canManage}>
                      {DESTINATION_THEMES.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <div className="marketing-details-section-head">
                    <span>3</span>
                    <div><h3>Promotion &amp; Schedule</h3><p>Configure discount terms and the active campaign window.</p></div>
                  </div>
                  <label>
                    Discount type
                    <select value={form.discountType} onChange={(event) => updateField("discountType", event.target.value)} disabled={!canManage}>
                      {DISCOUNT_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </select>
                    {form.discountType === "none" ? (
                      <span className="marketing-field-note">No discount will be applied for this campaign.</span>
                    ) : null}
                  </label>
                  {form.discountType !== "none" ? (
                    <label>
                      {form.discountType === "percentage" ? "Discount percentage" : "Fixed discount amount"}
                      <span className={`marketing-affixed-input ${form.discountType === "fixed" ? "has-prefix" : "has-suffix"}`}>
                        {form.discountType === "fixed" ? <span>PHP</span> : null}
                        <input
                          type="number"
                          min="1"
                          max={form.discountType === "percentage" ? "100" : undefined}
                          step="1"
                          inputMode="numeric"
                          placeholder={form.discountType === "percentage" ? "Enter percentage" : "Enter amount"}
                          value={form.discountValue}
                          onChange={(event) => updateField("discountValue", event.target.value)}
                          disabled={!canManage}
                        />
                        {form.discountType === "percentage" ? <span>%</span> : null}
                      </span>
                      <span className="marketing-field-note">
                        {form.discountType === "percentage" ? "Example: 10%" : "Example: PHP 500"}
                      </span>
                      {errors.discountValue ? <small>{errors.discountValue}</small> : null}
                    </label>
                  ) : null}
                  {form.discountType !== "none" ? (
                    <label>
                      {form.discountType === "percentage" ? "Promo code optional" : "Promo code"}
                      <input value={form.promoCode} onChange={(event) => updateField("promoCode", event.target.value.toUpperCase())} disabled={!canManage} />
                      <span className="marketing-field-note">
                        {form.discountType === "percentage"
                          ? "Customers can use this code to apply the percentage discount."
                          : "Customers can use this code to apply the fixed discount amount."}
                      </span>
                      {errors.promoCode ? <small>{errors.promoCode}</small> : null}
                    </label>
                  ) : null}
                  <label>
                    Start date
                    <input type="date" min={getTodayInputDate()} value={form.startDate} onChange={(event) => updateField("startDate", event.target.value)} disabled={!canManage} />
                    {errors.startDate ? <small>{errors.startDate}</small> : null}
                  </label>
                  <label>
                    Start time
                    <input type="time" value={form.startTime} onChange={(event) => updateField("startTime", event.target.value)} disabled={!canManage} />
                  </label>
                  <label>
                    End date
                    <input type="date" min={form.startDate || getTodayInputDate()} value={form.endDate} onChange={(event) => updateField("endDate", event.target.value)} disabled={!canManage} />
                    {errors.endDate ? <small>{errors.endDate}</small> : null}
                  </label>
                  <label>
                    End time
                    <input type="time" value={form.endTime} onChange={(event) => updateField("endTime", event.target.value)} disabled={!canManage} />
                    {errors.endTime ? <small>{errors.endTime}</small> : null}
                  </label>

                  <div className="marketing-details-section-head">
                    <span>4</span>
                    <div><h3>Vehicle Selection</h3><p>Choose the vehicles associated with this campaign.</p></div>
                  </div>
                  <div className="marketing-field marketing-field--full marketing-vehicles-field">
                    <div className="marketing-vehicles-field__head">
                      <span className="marketing-vehicles-field__label">Selected vehicles</span>
                      <span className="marketing-vehicles-field__count">
                        {form.selectedVehicles.length > 0
                          ? `${form.selectedVehicles.length} selected`
                          : "None selected"}
                      </span>
                    </div>
                    {vehicles.length > 6 ? (
                      <input
                        type="search"
                        className="marketing-vehicles-search"
                        placeholder="Search vehicles…"
                        value={vehicleSearch}
                        onChange={(e) => setVehicleSearch(e.target.value)}
                        disabled={!canManage}
                      />
                    ) : null}
                    <div className="marketing-vehicles-checklist">
                      {vehicles.length ? (() => {
                        const q = vehicleSearch.trim().toLowerCase();
                        const filtered = q
                          ? vehicles.filter((v) => {
                              const name = getVehicleName(v).toLowerCase();
                              const plate = String(v.plateNo || "").toLowerCase();
                              return name.includes(q) || plate.includes(q);
                            })
                          : vehicles;
                        if (!filtered.length) {
                          return <p className="marketing-vehicles-field__hint">No vehicles match your search.</p>;
                        }
                        return filtered.map((vehicle) => {
                          const id = String(vehicle._id);
                          const checked = form.selectedVehicles.includes(id);
                          const img = getVehicleImage(vehicle);
                          return (
                            <label key={id} className={`marketing-vehicle-card${checked ? " is-selected" : ""}${!canManage ? " is-disabled" : ""}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!canManage}
                                onChange={() => {
                                  const next = checked
                                    ? form.selectedVehicles.filter((v) => v !== id)
                                    : [...form.selectedVehicles, id];
                                  updateField("selectedVehicles", next);
                                }}
                              />
                              {img ? (
                                <div className="marketing-vehicle-card__thumb">
                                  <img src={img} alt={getVehicleName(vehicle)} />
                                </div>
                              ) : (
                                <div className="marketing-vehicle-card__thumb marketing-vehicle-card__thumb--placeholder">
                                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="1" y="8" width="22" height="10" rx="2"/>
                                    <path d="M5 8V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2"/>
                                    <circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>
                                  </svg>
                                </div>
                              )}
                              <div className="marketing-vehicle-card__body">
                                <strong>{getVehicleName(vehicle)}</strong>
                                {vehicle.plateNo ? <small>{vehicle.plateNo}</small> : null}
                              </div>
                              {checked ? (
                                <span className="marketing-vehicle-card__check" aria-hidden="true">
                                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 8l3.5 3.5L13 5"/>
                                  </svg>
                                </span>
                              ) : null}
                            </label>
                          );
                        });
                      })() : <p className="marketing-vehicles-field__hint">No vehicles available.</p>}
                    </div>
                  </div>

                  <div className="marketing-details-section-head">
                    <span>5</span>
                    <div><h3>Campaign Content</h3><p>Add the customer-facing copy and campaign artwork.</p></div>
                  </div>
                  <label className="marketing-field marketing-field--full">
                    Description
                    <textarea rows={3} value={form.description} onChange={(event) => updateField("description", event.target.value)} disabled={!canManage} />
                  </label>
                  <label className="marketing-field marketing-field--full">
                    Banner text
                    <input value={form.bannerText} onChange={(event) => updateField("bannerText", event.target.value)} disabled={!canManage} />
                  </label>
                  <label>
                    CTA text
                    <input value={form.ctaText} onChange={(event) => updateField("ctaText", event.target.value)} disabled={!canManage} />
                  </label>
                  <label>
                    Campaign image URL
                    <input
                      value={form.imageUrl}
                      onChange={(event) => {
                        updateField("imageUrl", event.target.value);
                        updateField("imageSource", event.target.value ? "external" : "");
                      }}
                      disabled={!canManage}
                    />
                    {errors.imageUrl ? <small>{errors.imageUrl}</small> : null}
                  </label>
                  <label className="marketing-field marketing-field--full">
                    Social caption
                    <textarea rows={3} value={form.socialCaption} onChange={(event) => updateField("socialCaption", event.target.value)} disabled={!canManage} />
                  </label>
                  <div className="marketing-field marketing-field--full marketing-upload-field">
                    <span className="marketing-vehicles-field__label">Campaign image upload</span>
                    {/* Hidden real file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: "none" }}
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        updateField("image", file);
                        updateField("imageSource", file ? "uploaded" : form.imageSource);
                      }}
                      disabled={!canManage}
                    />
                    {/* Preview: show if there is an existing URL or a freshly selected file */}
                    {(form.imageUrl && !form.image) || selectedImagePreviewUrl ? (
                      <div className="marketing-upload-preview">
                        {form.imageUrl && !form.image ? (
                          <img src={form.imageUrl} alt="Campaign preview" className="marketing-upload-preview__img" />
                        ) : null}
                        {selectedImagePreviewUrl ? (
                          <img src={selectedImagePreviewUrl} alt="Selected campaign preview" className="marketing-upload-preview__img" />
                        ) : null}
                        <div className="marketing-upload-preview__meta">
                          {form.image ? (
                            <span className="marketing-upload-preview__filename">
                              {form.imageSource === "generated" ? "Generated banner selected" : `Selected: ${form.image.name}`}
                            </span>
                          ) : (
                            <span className="marketing-upload-preview__filename">Existing image URL</span>
                          )}
                          {canManage ? (
                            <button
                              type="button"
                              className="marketing-upload-preview__replace"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Replace image
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`marketing-upload-drop${!canManage ? " is-disabled" : ""}`}
                        onClick={() => canManage && fileInputRef.current?.click()}
                        disabled={!canManage}
                      >
                        <span className="marketing-upload-drop__icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="3"/>
                            <path d="M3 15l5-5 4 4 3-3 6 6"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                          </svg>
                        </span>
                        <strong>Upload campaign image</strong>
                        <span>PNG, JPG or WebP recommended</span>
                        <span className="marketing-upload-drop__btn">Choose image</span>
                      </button>
                    )}
                    {form.imageSource === "generated" && form.image ? (
                      <span className="marketing-field-note">Banner selected for this campaign. Save the campaign to upload it.</span>
                    ) : null}
                    {errors.image ? <small style={{ color: "#dc2626", fontWeight: 800 }}>{errors.image}</small> : null}
                  </div>
                </div>
              ) : null}

              {/* ── Tab: Content ── */}
              {modalTab === "content" ? (
                <div className="marketing-modal__body marketing-modal__body--padded">
                  {canManage ? (
                    <div className="marketing-tab-toolbar">
                      <button
                        type="button"
                        className="marketing-btn marketing-btn--primary"
                        onClick={generateContent}
                        disabled={generatingContent}
                      >
                        {generatingContent ? "Generating…" : "Generate Content"}
                      </button>
                      <span className="marketing-tab-toolbar__note">
                        Content suggestions are generated from the campaign settings in Details.
                      </span>
                    </div>
                  ) : null}

                  {contentPreview ? (
                    <>
                      <div className="marketing-preview-grid">
                        <div><span>Title</span><p>{contentPreview.title}</p></div>
                        <div><span>Banner</span><p>{contentPreview.bannerText}</p></div>
                        <div><span>Description</span><p>{contentPreview.description}</p></div>
                        <div><span>CTA</span><p>{contentPreview.ctaText}</p></div>
                        <div className="marketing-preview-grid__full"><span>Social caption</span><p>{contentPreview.socialCaption}</p></div>
                        <div className="marketing-preview-grid__full">
                          <span>Suggested image</span>
                          {contentPreview.imageUrl ? (
                            <div className="marketing-image-suggestion">
                              <img src={contentPreview.imageUrl} alt="Suggested campaign visual" />
                              <p>Suggested from selected vehicle or matching category.</p>
                            </div>
                          ) : (
                            <p>No vehicle image suggestion available. You may upload a campaign image.</p>
                          )}
                        </div>
                      </div>
                      <div className="marketing-tab-actions">
                        <button type="button" className="marketing-btn marketing-btn--primary" onClick={applyGeneratedContent}>
                          Apply to Campaign
                        </button>
                        <button type="button" className="marketing-btn" onClick={generateContent} disabled={generatingContent}>
                          Regenerate
                        </button>
                        <button type="button" className="marketing-btn" onClick={() => setContentPreview(null)}>
                          Discard
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="marketing-tab-empty">
                      <strong>No generated content yet.</strong>
                      <p>Generate content suggestions for this campaign based on its type, audience, and theme.</p>
                    </div>
                  )}
                </div>
              ) : null}

              {/* ── Tab: Banner ── */}
              {modalTab === "banner" ? (
                <div className="marketing-modal__body marketing-modal__body--padded">
                  <div className="marketing-tab-toolbar">
                    <button
                      type="button"
                      className="marketing-btn marketing-btn--primary"
                      onClick={openBannerGenerator}
                      disabled={!canManage}
                    >
                      {generatingBanner ? "Generating…" : bannerPreview ? "Regenerate Banner" : "Generate Banner"}
                    </button>
                    <span className="marketing-tab-toolbar__note">
                      Creates a 1440 × 500 px FleetX banner from this campaign.
                    </span>
                  </div>

                  <div className="marketing-banner-controls">
                    <label>
                      Template
                      <select
                        value={bannerTemplate}
                        onChange={(event) => setBannerTemplate(event.target.value)}
                        disabled={!canManage}
                      >
                        {BANNER_TEMPLATES.map((template) => (
                          <option key={template.key} value={template.key}>{template.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Image source
                      <select
                        value={bannerImageSource}
                        onChange={(event) => {
                          const value = event.target.value;
                          setBannerImageSource(value);
                          if (value === "vehicle" && !getBannerVehicleImage(form, vehicles)) {
                            setBannerMessage("No vehicle image available. A branded background will be used.");
                          } else {
                            setBannerMessage("");
                          }
                        }}
                        disabled={!canManage}
                      >
                        <option value="vehicle">Use selected vehicle image</option>
                        <option value="custom">Upload custom image</option>
                        <option value="gradient">Use FleetX gradient / no image</option>
                      </select>
                    </label>
                    {bannerImageSource === "custom" ? (
                      <label>
                        Custom banner image
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(event) => handleBannerCustomImage(event.target.files?.[0] || null)}
                          disabled={!canManage}
                        />
                      </label>
                    ) : null}
                  </div>

                  {bannerMessage ? <p className="marketing-banner-message">{bannerMessage}</p> : null}

                  <div className="marketing-banner-preview">
                    {bannerPreview?.previewUrl ? (
                      <img src={bannerPreview.previewUrl} alt="Generated campaign banner preview" />
                    ) : (
                      <div>
                        <strong>Preview will appear here</strong>
                        <span>Choose a template, then click Generate Banner.</span>
                      </div>
                    )}
                  </div>

                  <div className="marketing-tab-actions">
                    <button
                      type="button"
                      className="marketing-btn marketing-btn--primary"
                      onClick={generateBannerPreview}
                      disabled={!canManage || generatingBanner}
                    >
                      {generatingBanner ? "Generating…" : bannerPreview ? "Regenerate" : "Generate Preview"}
                    </button>
                    {bannerPreview ? (
                      <button
                        type="button"
                        className="marketing-btn marketing-btn--activate"
                        onClick={applyGeneratedBanner}
                        disabled={form.image === bannerPreview.file}
                      >
                        {form.image === bannerPreview.file ? "Banner Selected" : "Use this Banner"}
                      </button>
                    ) : null}
                  </div>
                  {form.imageSource === "generated" && form.image ? (
                    <p className="marketing-banner-message">Banner selected for this campaign. Save Campaign will persist it.</p>
                  ) : null}
                </div>
              ) : null}

              {/* ── Tab: Performance ── */}
              {modalTab === "performance" && editingCampaign ? (
                <div className="marketing-modal__body marketing-modal__body--padded">
                  {editingPerformance ? (
                    <>
                      <p className="marketing-tab-desc">
                        All-time views, clicks, bookings, and revenue generated by this campaign.
                      </p>
                      <div className="marketing-perf-grid">
                        <div><span>Total views</span><strong>{formatNumber(editingPerformance.views)}</strong></div>
                        <div><span>Total clicks</span><strong>{formatNumber(editingPerformance.clicks)}</strong></div>
                        <div><span>CTR</span><strong>{formatPercent(editingPerformance.ctr)}</strong></div>
                        <div><span>Bookings generated</span><strong>{formatNumber(editingPerformance.bookingsFromCampaign)}</strong></div>
                        <div><span>Revenue generated</span><strong>{formatPeso(editingPerformance.revenueFromCampaign)}</strong></div>
                        <div><span>Discount given</span><strong>{formatPeso(editingPerformance.discountGiven)}</strong></div>
                        <div><span>Avg booking value</span><strong>{formatPeso(editingPerformance.averageBookingValue)}</strong></div>
                        <div><span>Conversion rate</span><strong>{formatPercent(editingPerformance.conversionRate)}</strong></div>
                        <div><span>Status</span><strong>{titleCase(editingPerformance.status)}</strong></div>
                        <div><span>Active range</span><strong>{formatDate(editingPerformance.activeDateRange?.startDate)} – {formatDate(editingPerformance.activeDateRange?.endDate)}</strong></div>
                        <div><span>Last viewed</span><strong>{formatDateTime(editingCampaign.lastViewedAt)}</strong></div>
                        <div><span>Last clicked</span><strong>{formatDateTime(editingCampaign.lastClickedAt)}</strong></div>
                      </div>
                      {editingPerformance.insight ? (
                        <p className="marketing-performance-insight">{editingPerformance.insight}</p>
                      ) : null}
                    </>
                  ) : (
                    <div className="marketing-tab-empty">
                      <strong>No performance data yet.</strong>
                      <p>Views and clicks will appear once this campaign is shown to customers.</p>
                    </div>
                  )}
                </div>
              ) : null}

              {/* ── Sticky footer ── */}
              <footer className="marketing-modal__footer">
                <button type="button" className="marketing-btn" onClick={requestCloseModal}>
                  Cancel
                </button>
                {canManage ? (
                  <button type="submit" className="marketing-btn marketing-btn--primary" disabled={saving}>
                    {saving ? "Saving…" : "Save Campaign"}
                  </button>
                ) : null}
              </footer>

              {/* ── Discard confirmation ── */}
              {discardConfirmOpen ? (
                <div
                  className="marketing-confirm-overlay marketing-confirm-overlay--inner"
                  onClick={() => setDiscardConfirmOpen(false)}
                >
                  <div
                    className="marketing-confirm-box"
                    role="dialog"
                    aria-modal="true"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h4>Discard unsaved changes?</h4>
                    <p>Any unsaved edits to this campaign will be lost.</p>
                    <div className="marketing-confirm-box__actions">
                      <button type="button" className="marketing-btn" onClick={() => setDiscardConfirmOpen(false)}>
                        Keep editing
                      </button>
                      <button type="button" className="marketing-btn marketing-btn--archive" onClick={confirmCloseModal}>
                        Discard changes
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

            </form>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
